import { NavLink } from 'react-router-dom';
import { useModulos } from '@/hooks/useModulos';
import {
  LayoutDashboard, Calendar, Users, Dog, Scissors, Settings, BarChart3,
  Hotel, Sparkles, ClipboardList, CreditCard, UserCog, HeartHandshake,
  Receipt, Package, Wallet, Bell, Building2, ShieldCheck, UserCircle, FileCheck2, Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import { GroomingProWordmark, GroomingProLogo } from './GroomingProLogo';
import { useUiStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { can } from '@/lib/permissions';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  badge?: string;
  /**
   * Módulo al que pertenece. Si la empresa no lo tiene contratado, la entrada
   * no se pinta. Sin módulo = siempre visible (Mi cuenta, Configuración…).
   */
  /** Si son varios, basta con tener uno (Servicios: peluquería o VetCare). */
  modulo?: string | string[];
  /** Permiso para verla: lo clínico solo lo ve quien puede leer la historia. */
  permiso?: string;
  /** Se esconde si la empresa tiene este módulo (evita la misma entrada dos veces). */
  ocultarSi?: string;
  /**
   * Si el módulo se vende aparte: la entrada solo sale cuando se SABE que la
   * empresa lo tiene, no mientras carga ni si la consulta falla.
   */
  estricto?: boolean;
};

type NavSection = {
  title: string;
  accent?: string;          // section color theme (border + icon bg)
  icon?: LucideIcon;        // pillar icon
  items: NavItem[];
};

/**
 * Sidebar organized into visually-distinct pillars so the user knows exactly
 * where each business line lives. Grooming and Hotel & Daycare are kept
 * apart with colored section headers — they look like "two products inside
 * one app" on purpose.
 */
const NAV: NavSection[] = [
  {
    title: 'Panel principal',
    items: [
      { to: '/dashboard', label: 'Operations Hub', icon: LayoutDashboard },
      // La agenda es del núcleo y va fuera de «Grooming»: una veterinaria
      // agenda consultas sin tener peluquería.
      { to: '/agenda',    label: 'Agenda',         icon: Calendar },
      { to: '/reports',   label: 'Reportes',       icon: BarChart3, modulo: 'reports' },
    ],
  },
  {
    title: 'Grooming',
    accent: 'emerald',
    icon: Scissors,
    items: [
      { to: '/services',  label: 'Servicios',  icon: Scissors, modulo: 'grooming' },
    ],
  },
  {
    // VetCare: lo clínico, separado de la peluquería como un pilar más.
    title: 'Clínica',
    accent: 'sky',
    icon: Stethoscope,
    items: [
      { to: '/clinica', label: 'Consultas', icon: Stethoscope, modulo: 'vetcare', permiso: 'clinical:read' },
      // Sin peluquería, el catálogo de servicios (consultas, vacunación…) vive
      // aquí y no bajo «Grooming»; con peluquería ya está allá.
      { to: '/services', label: 'Servicios', icon: ClipboardList, modulo: 'vetcare', ocultarSi: 'grooming' },
    ],
  },
  {
    title: 'Hotel & Daycare',
    accent: 'amber',
    icon: Hotel,
    items: [
      { to: '/hotel',       label: 'Hotel',       icon: Hotel, modulo: 'hotel' },
      { to: '/daycare',     label: 'Guardería',     icon: Sparkles, modulo: 'daycare' },
      { to: '/memberships', label: 'Membresías',  icon: HeartHandshake, modulo: 'memberships' },
    ],
  },
  {
    title: 'Clientes',
    items: [
      { to: '/customers', label: 'Clientes',  icon: Users, modulo: 'customers' },
      { to: '/reminders', label: 'Recordatorios', icon: Bell },
      { to: '/pets',      label: 'Mascotas',  icon: Dog, modulo: 'customers' },
    ],
  },
  {
    title: 'Operación',
    items: [
      { to: '/pos',            label: 'POS',            icon: CreditCard, modulo: 'pos' },
      { to: '/products',       label: 'Productos',      icon: Package, modulo: 'inventory' },
      { to: '/finance',        label: 'Compras y gastos', icon: Wallet, modulo: 'finance' },
      { to: '/facturacion',    label: 'Facturación electrónica', icon: FileCheck2, modulo: 'facturacion_electronica', estricto: true },
      { to: '/staff',          label: 'Staff',          icon: UserCog },
      { to: '/portal-preview', label: 'Portal cliente', icon: ClipboardList, modulo: 'portal' },
      { to: '/account',        label: 'Mi cuenta',      icon: UserCircle },
      { to: '/subscription',   label: 'Mi suscripción', icon: Receipt },
      { to: '/settings',       label: 'Configuración',  icon: Settings },
    ],
  },
];

/**
 * Lo que ve el dueño de la plataforma, ADEMÁS de todo lo demás.
 *
 * Esta sección se suma al menú completo, no lo reemplaza: el
 * superadministrador entra al sistema para probarlo, así que necesita abrir
 * la agenda, el POS o el hotel como los abriría una peluquería. Dejarle solo
 * la administración le impedía comprobar que lo que vende funciona.
 */
const NAV_SUPERADMIN: NavSection[] = [
  {
    title: 'Plataforma',
    accent: 'violet',
    icon: ShieldCheck,
    items: [
      { to: '/admin',   label: 'Empresas y planes', icon: Building2 },
      { to: '/account', label: 'Mi cuenta',         icon: UserCircle },
    ],
  },
];

const PILLAR_STYLES: Record<string, { ring: string; iconBg: string; iconText: string; titleText: string; bg: string }> = {
  emerald: {
    ring:      'ring-emerald-200',
    iconBg:    'bg-emerald-500',
    iconText:  'text-white',
    titleText: 'text-emerald-900',
    bg:        'bg-emerald-50/60',
  },
  amber: {
    ring:      'ring-amber-200',
    iconBg:    'bg-amber-500',
    iconText:  'text-white',
    titleText: 'text-amber-900',
    bg:        'bg-amber-50/60',
  },
  sky: {
    ring:      'ring-sky-200',
    iconBg:    'bg-sky-500',
    iconText:  'text-white',
    titleText: 'text-sky-900',
    bg:        'bg-sky-50/60',
  },
  violet: {
    ring:      'ring-violet-200',
    iconBg:    'bg-violet-500',
    iconText:  'text-white',
    titleText: 'text-violet-900',
    bg:        'bg-violet-50/60',
  },
};

interface SidebarProps {
  /**
   * `chrome` = standalone aside with sticky positioning + size + border (default,
   *   used inside the desktop layout). `bare` = strip the chrome so the parent
   *   (e.g. a Sheet drawer) can position it.
   */
  variant?: 'chrome' | 'bare';
  /** Called after a NavLink is clicked — used by the drawer to auto-close. */
  onNavigate?: () => void;
}

export function Sidebar({ variant = 'chrome', onNavigate }: SidebarProps = {}) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const isBare = variant === 'bare';
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  // El superadministrador sin empresa ve solo lo suyo. Si algún día se le
  // asigna una (para dar soporte desde dentro), ve las dos cosas.
  // El superadministrador ve su sección primero y luego el sistema entero.
  // El menú solo ofrece lo que la empresa contrató. Antes listaba las 17
  // rutas para todos, así que un plan sin Hotel igual lo mostraba y lo usaba:
  // el plan no limitaba nada.
  const { tiene, tieneConfirmado } = useModulos();
  const permisos = useAuthStore((s) => s.user?.permissions ?? []);
  const visible = (it: NavItem) =>
    (!it.permiso || can(permisos, it.permiso))
    && (!it.ocultarSi || !tieneConfirmado(it.ocultarSi))
    && (it.estricto && typeof it.modulo === 'string' ? tieneConfirmado(it.modulo) : tiene(it.modulo));
  const secciones = (isSuperAdmin ? [...NAV_SUPERADMIN, ...NAV] : NAV)
    .map((sec) => ({ ...sec, items: sec.items.filter(visible) }))
    // Una sección sin entradas deja un título suelto y confunde.
    .filter((sec) => sec.items.length > 0);

  return (
    <aside
      className={cn(
        'flex h-full flex-col bg-card transition-all duration-200',
        // The drawer host already provides positioning/borders.
        !isBare && 'sticky top-0 h-screen border-r border-border',
        !isBare && (collapsed ? 'w-[72px]' : 'w-[272px]'),
        isBare && 'w-full',
      )}
    >
      <div className="flex h-16 items-center px-4">
        {!isBare && collapsed ? <GroomingProLogo /> : <GroomingProWordmark />}
      </div>

      <nav className="flex-1 space-y-3 overflow-y-auto px-3 pb-2">
        {secciones.map((section, sIdx) => {
          const styles = section.accent ? PILLAR_STYLES[section.accent] : null;
          const isPillar = Boolean(section.accent);
          // In bare (drawer) mode collapsed visuals don't apply.
          const isCollapsed = collapsed && !isBare;

          return (
            <div
              key={sIdx}
              className={cn(
                isPillar && !isCollapsed && styles && `rounded-2xl ring-1 ${styles.ring} ${styles.bg} p-2`,
              )}
            >
              {/* Section header */}
              {!isCollapsed && (
                <div className={cn('mb-1.5 flex items-center gap-2 px-2', isPillar && 'py-1')}>
                  {section.icon && isPillar && styles && (
                    <div className={cn('flex h-5 w-5 items-center justify-center rounded-md', styles.iconBg, styles.iconText)}>
                      <section.icon className="h-3 w-3" />
                    </div>
                  )}
                  <p
                    className={cn(
                      'text-[10px] font-bold uppercase tracking-wider',
                      isPillar && styles ? styles.titleText : 'text-muted-foreground',
                    )}
                  >
                    {section.title}
                  </p>
                </div>
              )}

              {isCollapsed && isPillar && <div className="my-2 h-px bg-border mx-3" />}

              <div className="space-y-0.5">
                {section.items.map(({ to, label, icon: Icon, disabled, badge }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={(e) => {
                      if (disabled) { e.preventDefault(); return; }
                      onNavigate?.();
                    }}
                    className={({ isActive }) =>
                      cn(
                        'sidebar-item min-h-[44px]',
                        isActive && !disabled && 'bg-primary/10 text-primary',
                        disabled && 'pointer-events-none opacity-40',
                        isCollapsed && 'justify-center px-2',
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!isCollapsed && <span className="flex-1 truncate">{label}</span>}
                    {!isCollapsed && badge && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider',
                          badge === 'NUEVO'   && 'bg-primary/15 text-primary',
                          badge === 'BETA'    && 'bg-amber-100 text-amber-800',
                          badge === 'PREVIEW' && 'bg-purple-100 text-purple-800',
                        )}
                      >
                        {badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-3">
        <div
          className={cn(
            'rounded-xl border border-border bg-gradient-to-br from-emerald-50 to-teal-50 p-3 text-xs',
            collapsed && !isBare && 'hidden',
          )}
        >
          <p className="font-semibold text-emerald-900">Grooming ERP</p>
          <p className="mt-1 text-muted-foreground">
            Cada negocio con su propio espacio: peluquería, clínica y hotel.
          </p>
        </div>
      </div>
    </aside>
  );
}
